/**
 * IconToggleGroup - Icon button group for toggle selections
 *
 * Displays a horizontal row of icon buttons for selecting options.
 * Used for alignment, object-fit, direction, etc.
 */

import React from 'react';
import * as Icons from 'lucide-react';

/**
 * IconToggleGroup Component
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration { key, options, ... }
 * @param {*} props.value - Current selected value
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} [props.className] - Additional classes
 */
export default function IconToggleGroup({
  field,
  value,
  onChange,
  compact = false,
  className = '',
}) {
  const { options = [] } = field;

  // Handle responsive values (object with base/tablet/desktop)
  const currentValue = typeof value === 'object' && value?.base !== undefined
    ? value.base
    : value;

  const handleSelect = (optionValue) => {
    // For responsive values, update only the base value
    if (typeof value === 'object' && value?.base !== undefined) {
      onChange({
        ...value,
        base: optionValue,
      });
    } else {
      onChange(optionValue);
    }
  };

  const toggleGroup = (
    <div className={`flex items-center gap-0.5 bg-gray-100 rounded p-0.5 ${!compact ? className : ''}`}>
      {options.map((option) => {
        const Icon = option.icon ? Icons[option.icon] : null;
        const isActive = currentValue === option.value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option.value)}
            className={`
              p-1 rounded transition-colors flex items-center justify-center h-5
              ${isActive
                ? 'bg-white shadow-sm text-gray-900'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }
            `}
            title={option.label || option.value}
          >
            {Icon ? (
              <Icon className="w-3 h-3" />
            ) : (
              <span className="text-[10px] leading-none font-medium px-0.5">{option.label || option.value}</span>
            )}
          </button>
        );
      })}
    </div>
  );

  // Compact mode: show label + toggle group inline (matches other compact fields)
  if (compact && field.label) {
    return (
      <div className={`flex items-center gap-2 py-0.5 ${className}`}>
        <label className="text-xs text-gray-500 w-16 flex-shrink-0 truncate">
          {field.label}
        </label>
        <div className="flex-1">
          {toggleGroup}
        </div>
      </div>
    );
  }

  return toggleGroup;
}
